import React, { useState } from 'react';
import { Handshake, Lock, Check, X, Building2, TrendingUp, Landmark } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import {
  COUT_STRUCTURATION_FRANCHISE, DROITS_ENTREE, REDEVANCES_PCT,
  franchiseDebloquee, attractiviteLabel, villesLibresFranchise,
  JOURS_MIN_EXPLOITATION, MAGASINS_MIN, VILLES_MIN,
} from '../../utils/franchise';

const eur = (n) => (n || 0).toLocaleString('fr-FR');
const TONE = { money: 'text-money', accent: 'text-accent', warn: 'text-amber-400', danger: 'text-danger' };

export default function FranchisePanel() {
  const retail = useGameStore(s => s.retail);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const treasury = useGameStore(s => s.treasury || 0);
  const creerReseauFranchise = useGameStore(s => s.creerReseauFranchise);
  const setConditionsFranchise = useGameStore(s => s.setConditionsFranchise);
  const repondreCandidatureFranchise = useGameStore(s => s.repondreCandidatureFranchise);
  const [info, setInfo] = useState(null);
  if (!retail) return null;

  const fr = retail.franchise;
  const unlock = franchiseDebloquee(retail, totalDays);

  /* ── Pas encore structuré ── */
  if (!fr?.cree) {
    return (
      <div className="rounded-xl border border-ink-600 bg-ink-800/60 p-5 space-y-4" data-testid="franchise-panel-locked">
        <div className="flex items-center gap-2">
          <Handshake className="w-5 h-5 text-amber-400" />
          <h3 className="font-black text-foreground">Créer votre franchise</h3>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed">
          Votre concept a fait ses preuves ? Structurez-le en réseau de franchise : des commerçants indépendants
          ouvriront sous votre enseigne, verseront un droit d'entrée puis une redevance mensuelle sur leur résultat.
        </p>
        <div className="space-y-1.5 text-xs" data-testid="franchise-conditions">
          <p className={unlock.anciennete ? 'text-money' : 'text-slate-500'}>
            {unlock.anciennete ? '✓' : '○'} Au moins 1 an d'exploitation ({Math.min(totalDays, JOURS_MIN_EXPLOITATION)}/{JOURS_MIN_EXPLOITATION} jours)
          </p>
          <p className={unlock.magasins >= MAGASINS_MIN ? 'text-money' : 'text-slate-500'}>
            {unlock.magasins >= MAGASINS_MIN ? '✓' : '○'} Au moins {MAGASINS_MIN} magasins en propre ({unlock.magasins}/{MAGASINS_MIN})
          </p>
          <p className={unlock.villes >= VILLES_MIN ? 'text-money' : 'text-slate-500'}>
            {unlock.villes >= VILLES_MIN ? '✓' : '○'} Présent dans au moins {VILLES_MIN} villes ({unlock.villes}/{VILLES_MIN})
          </p>
        </div>
        <div className="rounded-lg border border-ink-600 bg-ink-900/50 p-3 text-xs text-slate-300">
          Coût de structuration (manuel opératoire, concept, juridique) : <strong className="text-foreground">{eur(COUT_STRUCTURATION_FRANCHISE)} €</strong>
        </div>
        <div className="flex gap-2">
          <button
            disabled={!unlock.ok || treasury < COUT_STRUCTURATION_FRANCHISE}
            onClick={() => { const r = creerReseauFranchise('tresorerie'); setInfo(r.ok ? null : r.error); }}
            className="px-3 py-2 rounded-md bg-amber-500 text-ink-900 text-xs font-black disabled:opacity-40"
            data-testid="franchise-creer-tresorerie"
          >Payer comptant ({eur(COUT_STRUCTURATION_FRANCHISE)} €)</button>
          <button
            disabled={!unlock.ok}
            onClick={() => { const r = creerReseauFranchise('emprunt'); setInfo(r.ok ? null : r.error); }}
            className="px-3 py-2 rounded-md border border-amber-500/50 text-amber-400 text-xs font-black disabled:opacity-40"
            data-testid="franchise-creer-emprunt"
          ><Landmark className="w-3.5 h-3.5 inline mr-1" />Financer par emprunt (36 mois)</button>
        </div>
        {info && <p className="text-xs text-danger">{info}</p>}
        {!unlock.ok && <p className="text-[11px] text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Remplissez les trois conditions pour structurer votre réseau.</p>}
      </div>
    );
  }

  /* ── Réseau actif ── */
  const attr = attractiviteLabel(fr.droitEntree, fr.redevancePct);
  const libres = villesLibresFranchise(retail).length;
  return (
    <div className="space-y-4" data-testid="franchise-panel">
      {/* Conditions proposées */}
      <div className="rounded-xl border border-ink-600 bg-ink-800/60 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-foreground flex items-center gap-2"><Handshake className="w-4 h-4 text-amber-400" /> Vos conditions de franchise</h3>
          <span className={`text-xs font-bold ${TONE[attr.tone]}`} data-testid="franchise-attractivite">{attr.label} · {attr.detail}</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Droit d'entrée (à la signature)</p>
            <div className="flex gap-1.5">
              {DROITS_ENTREE.map(d => (
                <button key={d.montant}
                  onClick={() => setConditionsFranchise({ droitEntree: d.montant, redevancePct: fr.redevancePct })}
                  className={`flex-1 px-2 py-1.5 rounded-md text-xs font-bold border transition-colors ${fr.droitEntree === d.montant ? 'bg-amber-500/15 border-amber-500 text-amber-300' : 'border-ink-600 text-slate-400 hover:border-ink-500'}`}
                  data-testid={`franchise-droit-${d.montant}`}
                >{eur(d.montant)} €<span className="block text-[9px] font-normal opacity-70">{d.label}</span></button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Redevance mensuelle (% du résultat du franchisé)</p>
            <div className="flex gap-1.5">
              {REDEVANCES_PCT.map(rv => (
                <button key={rv.pct}
                  onClick={() => setConditionsFranchise({ droitEntree: fr.droitEntree, redevancePct: rv.pct })}
                  className={`flex-1 px-2 py-1.5 rounded-md text-xs font-bold border transition-colors ${fr.redevancePct === rv.pct ? 'bg-amber-500/15 border-amber-500 text-amber-300' : 'border-ink-600 text-slate-400 hover:border-ink-500'}`}
                  data-testid={`franchise-redevance-${rv.pct}`}
                >{rv.pct} %<span className="block text-[9px] font-normal opacity-70">{rv.label}</span></button>
              ))}
            </div>
          </div>
        </div>
        <p className="text-[11px] text-slate-500">Conditions douces = candidatures fréquentes · conditions dures = candidats rares mais plus lucratifs. {libres} villes encore ouvertes aux franchisés.</p>
      </div>

      {/* Candidature en cours */}
      {fr.candidature && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 space-y-2" data-testid="franchise-candidature">
          <p className="text-sm font-black text-foreground">🤝 {fr.candidature.nom} — {fr.candidature.villeNom}</p>
          <p className="text-xs text-slate-400">
            Résultat mensuel estimé : <strong className="text-foreground">{eur(fr.candidature.resultatMensuel)} €</strong> ·
            redevance attendue ≈ <strong className="text-money">{eur(Math.round(fr.candidature.resultatMensuel * fr.redevancePct / 100))} €/mois</strong> ·
            droit d'entrée : <strong className="text-money">{eur(fr.droitEntree)} €</strong>
          </p>
          <div className="flex gap-2">
            <button onClick={() => repondreCandidatureFranchise(true)} className="px-3 py-1.5 rounded-md bg-money/90 text-ink-900 text-xs font-black flex items-center gap-1" data-testid="franchise-accepter"><Check className="w-3.5 h-3.5" /> Accepter</button>
            <button onClick={() => repondreCandidatureFranchise(false)} className="px-3 py-1.5 rounded-md border border-ink-600 text-slate-400 text-xs font-bold flex items-center gap-1" data-testid="franchise-refuser"><X className="w-3.5 h-3.5" /> Refuser</button>
          </div>
        </div>
      )}

      {/* Réseau de franchisés */}
      <div className="rounded-xl border border-ink-600 bg-ink-800/60 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-foreground flex items-center gap-2"><Building2 className="w-4 h-4 text-amber-400" /> Franchisés ({(fr.franchises || []).length})</h3>
          <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="franchise-totaux">
            <TrendingUp className="w-3.5 h-3.5 text-money" />
            Droits d'entrée : <strong className="text-money">{eur(fr.totalDroitsEntree)} €</strong> · Redevances : <strong className="text-money">{eur(fr.totalRedevances)} €</strong>
          </span>
        </div>
        {(fr.franchises || []).length === 0 ? (
          <p className="text-xs text-slate-500">Aucun franchisé pour le moment — les candidatures arrivent par la messagerie selon l'attractivité de vos conditions.</p>
        ) : (() => {
          const franchises = fr.franchises;
          const moyenne = franchises.reduce((s, f) => s + f.resultatMensuel, 0) / franchises.length;
          const rows = [...franchises].sort((a, b) => b.resultatMensuel - a.resultatMensuel);
          const tendance = (f) => {
            if (f.resultatPrecedent == null || f.dernierResultat == null) return { icon: '—', cls: 'text-slate-500' };
            if (f.dernierResultat > f.resultatPrecedent) return { icon: '↗', cls: 'text-money' };
            if (f.dernierResultat < f.resultatPrecedent) return { icon: '↘', cls: 'text-danger' };
            return { icon: '→', cls: 'text-slate-400' };
          };
          return (
            <div className="overflow-x-auto" data-testid="franchise-comparateur">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 uppercase text-[10px] border-b border-ink-600">
                    <th className="py-1.5 pr-2">Franchisé</th>
                    <th className="py-1.5 pr-2">Ville</th>
                    <th className="py-1.5 pr-2">Ancienneté</th>
                    <th className="py-1.5 pr-2 text-right">Résultat mensuel</th>
                    <th className="py-1.5 pr-2 text-center">Tendance</th>
                    <th className="py-1.5 pr-2 text-right">Dernière redevance</th>
                    <th className="py-1.5 pr-2 text-right">Redevances cumulées</th>
                    <th className="py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(f => {
                    const t = tendance(f);
                    const aSoutenir = franchises.length > 1 && f.resultatMensuel < moyenne;
                    const mois = Math.floor(Math.max(0, totalDays - f.signeJour) / 30);
                    return (
                      <tr key={f.id} className="border-b border-ink-700/60" data-testid={`franchise-${f.villeId}`}>
                        <td className="py-1.5 pr-2 font-bold text-foreground">{f.nom}</td>
                        <td className="py-1.5 pr-2 text-slate-400">{f.villeNom}</td>
                        <td className="py-1.5 pr-2 font-mono text-slate-400">{mois} mois</td>
                        <td className={`py-1.5 pr-2 text-right font-mono ${aSoutenir ? 'text-amber-300' : 'text-slate-200'}`}>{eur(f.resultatMensuel)} €</td>
                        <td className={`py-1.5 pr-2 text-center font-bold ${t.cls}`}>{t.icon}</td>
                        <td className="py-1.5 pr-2 text-right font-mono text-money">{eur(f.derniereRedevance || 0)} €</td>
                        <td className="py-1.5 pr-2 text-right font-mono text-slate-300">{eur(f.totalRedevances || 0)} €</td>
                        <td className="py-1.5 text-right">
                          {aSoutenir && <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300" data-testid={`franchise-a-soutenir-${f.villeId}`}>À soutenir</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[10px] text-slate-500 mt-2">Moyenne du réseau : <strong className="text-slate-300">{eur(Math.round(moyenne))} €/mois</strong> — les franchisés sous la moyenne méritent une animation commerciale (leurs redevances suivront).</p>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
